import React, { useMemo, useState } from "react";
import { StyleSheet, Text, View, useWindowDimensions } from "react-native";
import { useSearchParams } from "react-router-dom";

import global from "../../styles/globalStyles";
import { ADMIN_TOKENS } from "../../styles/adminTokens";
import { supabase } from "../../utils/supabase";
import { mixpanel } from "../../utils/mixpanel";
import { getPostAuthRedirectPath } from "../../utils/authRedirect";
import {
  captureMarketingHandoffFromSearch,
  getMarketingHandoffContext,
} from "../../utils/marketingHandoff";

import {
  Header,
  Input,
  TextLink,
  FooterLinks,
  PrimaryButton,
  GoogleLoginBtn,
  SecondaryButton,
} from "../common/native/index";
import { Password } from "../../assets";
import DrFanRecommendation from "../common/native/DrFanRecommendation";

const Signup = () => {
  const { width } = useWindowDimensions();
  const isMobile = width < 850;
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errorMessageStep1, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [usernamePasswordSignUp, setUsernamePasswordSignUp] =
    useState<boolean>(false);
  const [doneSigningUp, setDoneSigningUp] = useState<boolean>(false);
  const oauthCallbackPath = useMemo(() => {
    const returnToPath = getPostAuthRedirectPath(searchParams.get("returnTo"));
    const [basePath, existingQuery = ""] = returnToPath.split("?");
    const nextQueryParams = new URLSearchParams(existingQuery);

    const marketingHandoff = getMarketingHandoffContext(searchParams);
    if (marketingHandoff?.intent) {
      nextQueryParams.set("intent", marketingHandoff.intent);
    }
    if (marketingHandoff?.primaryGoal) {
      nextQueryParams.set("primary_goal", marketingHandoff.primaryGoal);
    }
    if (marketingHandoff?.websiteUrl) {
      nextQueryParams.set("website", marketingHandoff.websiteUrl);
    }

    searchParams.forEach((value, key) => {
      if (key.toLowerCase().startsWith("utm_") && value.trim()) {
        nextQueryParams.set(key, value.trim());
      }
    });

    const nextReturnTo = nextQueryParams.toString()
      ? `${basePath}?${nextQueryParams.toString()}`
      : basePath;
    return `/signin?returnTo=${encodeURIComponent(nextReturnTo)}`;
  }, [searchParams]);

  React.useEffect(() => {
    captureMarketingHandoffFromSearch(searchParams);
  }, [searchParams]);

  const handleOnPressLogInWithEmail = async () => {
    setErrorMessage("");
    const trimedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters long.");
      return;
    }
    try {
      setLoading(true);
      const marketingHandoff = getMarketingHandoffContext(searchParams);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...(marketingHandoff?.intent
              ? { marketing_intent: marketingHandoff.intent }
              : {}),
            ...(marketingHandoff?.primaryGoal
              ? { primary_goal: marketingHandoff.primaryGoal }
              : {}),
            ...(marketingHandoff?.websiteUrl
              ? { website_url: marketingHandoff.websiteUrl }
              : {}),
          },
        },
      });

      if (!data || !data.user || !data.user.id) {
        throw error;
      }
      setDoneSigningUp(true);

      mixpanel.track("Signup Started", {
        "User Email": data.user.email,
      });
    } catch (error) {
      setErrorMessage("Error in Signing Up");
    } finally {
      setLoading(false);
    }
  };

  return (
    <View
      style={{
        flex: 1,
        flexDirection: "row",
        height: "100%",
      }}
    >
      <View style={styles.container}>
        <Header />
        {doneSigningUp ? (
          <View
            style={{
              width: "100%",
              maxWidth: 434,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={[
                global.title,
                { marginHorizontal: "auto", textAlign: "center" },
              ]}
            >
              Registered
            </Text>
            <Text style={global.text}>Check your email to confirm: </Text>
            <Text style={[global.text, global.fontWeight600]}>{email}</Text>
          </View>
        ) : (
          <View
            style={{
              width: "100%",
              maxWidth: 434,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <View style={{ gap: 10, width: "100%" }}>
              <Text
                style={[
                  global.title,
                  { marginHorizontal: "auto", textAlign: "center" },
                ]}
              >
                Sign Up & Get Started
              </Text>
            </View>
            <View style={styles.patientPrompt}>
              <Text
                style={{
                  fontFamily: "Urbanist600",
                  fontSize: 16,
                }}
              >
                Are you a Patient?
              </Text>
              <TextLink
                to={"/patient-signup"}
                text={"Switch here"}
                style={[global.blue, global.fontSize18]}
              />
            </View>

            <View style={styles.clinicClaimCard}>
              <View style={styles.clinicClaimHeader}>
                <Text style={styles.clinicClaimEyebrow}>CLINIC BONUS:</Text>
                <Text style={styles.clinicClaimTitle}>
                  Sign up using your Clinic Email account for an extra 7 days of
                  simulations.
                </Text>
              </View>
            </View>

            <View style={{ gap: 16, width: "100%" }}>
              <GoogleLoginBtn
                name={"Sign Up with Google"}
                trackSignupStarted
                redirectTo={oauthCallbackPath}
              />
              <GoogleLoginBtn
                name={"Sign Up with Apple"}
                provider="apple"
                trackSignupStarted
                redirectTo={oauthCallbackPath}
              />
              <SecondaryButton
                text={"Username/Password"}
                icon={<Password />}
                fullWidth
                style={{
                  background: usernamePasswordSignUp ? "#C4EEFD" : "#fff",
                }}
                onPress={() =>
                  setUsernamePasswordSignUp(!usernamePasswordSignUp)
                }
              />
            </View>
            {usernamePasswordSignUp && (
              <View style={{ gap: 8, width: "100%" }}>
                <Input
                  type="email-address"
                  name="Email"
                  value={email}
                  placeholder="Email"
                  onChangeText={setEmail}
                  required
                />
                <Input
                  type="default"
                  name="Password"
                  value={password}
                  placeholder="Password"
                  onChangeText={setPassword}
                  secureTextEntry
                  required
                />
                <Text
                  style={[
                    global.errorText,
                    {
                      marginBottom: 5,
                      marginHorizontal: "auto",
                      textAlign: "center",
                    },
                  ]}
                >
                  {errorMessageStep1}
                </Text>
                <PrimaryButton
                  text="Sign Up"
                  fullWidth
                  onPress={handleOnPressLogInWithEmail}
                  disabled={!email}
                  loading={loading}
                />
              </View>
            )}
            {isMobile && !usernamePasswordSignUp && (
              <View
                style={{
                  width: "100%",
                  marginTop: 40,
                }}
              >
                <DrFanRecommendation />
              </View>
            )}
            <View style={styles.signInPrompt}>
              <Text
                style={[global.text, global.fontWeight600, global.fontSize18]}
              >
                Already have an account?
              </Text>
              <TextLink
                to={"/signin"}
                text={"Sign In"}
                style={[global.blue, global.fontSize18]}
              />
            </View>
          </View>
        )}
        <FooterLinks />
      </View>
      {!isMobile && (
        <View
          style={{
            flex: 1,
            width: "35%",
            minWidth: 430,
            maxWidth: 550,
          }}
        >
          <DrFanRecommendation />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    gap: 40,
    padding: 16,
  },
  clinicClaimCard: {
    width: "100%",
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.borderAccent,
    borderRadius: ADMIN_TOKENS.radius.lg,
    backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
    padding: ADMIN_TOKENS.spacing.lg,
    gap: ADMIN_TOKENS.spacing.md,
    marginBottom: ADMIN_TOKENS.spacing.xxl,
  },
  clinicClaimHeader: {
    gap: ADMIN_TOKENS.spacing.xs,
  },
  clinicClaimEyebrow: {
    fontFamily: ADMIN_TOKENS.font.semibold,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    color: ADMIN_TOKENS.color.accentDark,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  clinicClaimTitle: {
    fontFamily: ADMIN_TOKENS.font.semibold,
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  patientPrompt: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 40,
  },
  signInPrompt: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 40,
  },
});

export default Signup;
